import React, { useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  SelectWithSearch,
  useToast,
} from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import {
  ToggleBaseMapInSettings,
  useWMSSettings,
} from '../../WMSLayers/useWMSSettings';
import { useSettings } from '../SettingsProvider';
import { useLocalization } from '@ais-ui/locale-management';

const addBaseMapFormSchema = z.object({
  wmsLayer: z.string().min(1),
});

type AddBaseMapFormValues = z.infer<typeof addBaseMapFormSchema>;

export const BaseMapAdd = () => {
  const { messages } = useLocalization();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const wmsSettings = useWMSSettings();

  const form = useForm<AddBaseMapFormValues>({
    resolver: zodResolver(addBaseMapFormSchema),
    defaultValues: {
      wmsLayer: '',
    },
  });

  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();

  async function onAddService(data: AddBaseMapFormValues) {
    setIsSubmitting(true);

    const updatedSettings = ToggleBaseMapInSettings(
      settings,
      wmsSettings,
      data.wmsLayer,
      true
    );

    // Save the updated settings
    mutateSettings(updatedSettings, {
      onSuccess: () => {
        toast({
          title: messages.genericToastTitleSuccess,
          description: messages.settingsBaseMapSuccessAddMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
      onError: () => {
        toast({
          title: messages.genericToastTitleError,
          description: messages.settingsBaseMapFailedAddMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  const selections = useMemo(() => {
    return wmsSettings.wmsInfos.flatMap((wmsLayer) => {
      const wmsName = wmsLayer.name;
      return (
        wmsLayer.layers?.map((layer) => ({
          id: layer.id,
          name: `${wmsName} - ${layer.name}`,
        })) || []
      );
    });
  }, [wmsSettings]);

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button>{messages.settingsBaseMapAddButtonText}</Button>
      </DialogTrigger>
      <DialogContent id="add-update-service-dialog">
        <DialogHeader>
          <DialogTitle>{messages.settingsBaseMapAddTitleText}</DialogTitle>
          <DialogDescription>
            {messages.settingsBaseMapAddSubtitleText}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onAddService)}
            className="space-y-8"
          >
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="wmsLayer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {messages.settingsBaseMapLayerLabel} *
                    </FormLabel>
                    <FormControl onChange={() => form.clearErrors()}>
                      <SelectWithSearch
                        value={field.value}
                        onChange={field.onChange}
                        data={selections}
                        placeholder={messages.settingsBaseMapPlaceholder}
                      ></SelectWithSearch>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter className="p-4">
              <Button
                id="base-map-confirm-add-button"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? messages.genericSubmitting
                  : messages.genericSubmit}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
